import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import {
  Form,
  Accordion,
  Icon,
  Table,
  Popup,
  Dropdown,
  FormTextArea,
  Modal,
  Button,
  Radio,
  TextAreaProps
} from "semantic-ui-react";
import { values } from "lodash";
import NumberField from "../../components/Editing/NumberField";
import MenuButton from "../../components/MenuButton/MenuButton";
import {
  getDuplicateLiftParameterBindings,
  createSwDefaultArray
} from "../../utils/pdApiUtils";
import styles from "./LiftParameterValues.module.scss";

export type LiftParameterValuesProps = {
  id: number;
  code: number;
  itemCount: number;
  minValue: number;
  maxValue: number;
  swDefaultValues: string;
  liftParameterValues: Api.LiftParameterValue[];
  kceProducts: Api.KceProducts;
  kceVersions: Api.KceVersions;
  handleDefaultValuesChanged: (defaultValues: string, liftValues: Api.LiftParameterValue[], minValue: number, maxValue: number, itemCount: number) => void;
  editorDefines: string | null;
  permissions: boolean;
};

// --- Types ---
type EditingEntry = { minValue: number; maxValue: number; itemCount: number; swDefaultValue: string };

type ModalState = {
  baseDefault: EditingEntry;
  bindings: (Api.LiftParameterValue & EditingEntry)[];
  /** -1 = base default selected, 0+ = binding index, null = nothing selected on right */
  editingIndex: number | null;
  pendingItemCount: number;
};

// --- Helpers ---
const MAX_ITEM_COUNT = 250;

const validate = (min: number, max: number, itm: number, sw: string, ascii = false) => ({
  minMaxErr: min > max ? "Min value must be less than or equal to Max value." : "",
  itmErr: itm < 1 ? "Item count must be greater than or equal to 1." : (itm > MAX_ITEM_COUNT ? `Item Count cannot exceed ${MAX_ITEM_COUNT}.` : ""),
  rangeErr: (() => {
    const parts = sw.split(";");
    return parts.some(d => Number(d) > max || Number(d) < min)
      ? ascii
        ? `Sw default value is out of range (${min}..${max}). Set the Min value to 0 and Max value to 4294967295 if it is unknown.`
        : `Sw default value is out of range (${min}..${max}).`
      : "";
  })()
});

const resize = (sw: string, size: number, fill: number): string => {
  let a = createSwDefaultArray(sw);
  if (size > a.length) a = [...a, ...new Array(size - a.length).fill(fill)];
  else a = a.slice(0, size);
  return a.join(";");
};

const asciiToSw = (v: string): string => v === "" ? "0" : v.split("").map(c => c.charCodeAt(0)).join(";");
const swToAscii = (sw: string): string => {
  if (sw === "0" || sw === "") return "";
  // Filter out null characters (char code 0) — they're used as placeholders, not display values
  return String.fromCharCode(...sw.split(";").filter(v => v !== "0").map(v => Number(v)));
};
const isAscii = (ed: string | null): boolean => ed !== null && ed.includes("2;;;ASCII");

const resolveEntry = (lv: Api.LiftParameterValue, pMin: number, pMax: number, pItm: number): EditingEntry => ({
  minValue: lv.minValue ?? pMin, maxValue: lv.maxValue ?? pMax,
  itemCount: lv.itemCount ?? pItm, swDefaultValue: lv.swDefaultValue
});

const LiftParameterValues = ({
  id, code, itemCount, minValue, maxValue, swDefaultValues,
  liftParameterValues, kceProducts, kceVersions,
  handleDefaultValuesChanged, editorDefines, permissions
}: LiftParameterValuesProps): JSX.Element => {
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modal, setModal] = useState<ModalState | null>(null);
  const [snapshot, setSnapshot] = useState<string | null>(null);
  const [confirmClose, setConfirmClose] = useState(false);

  // Reset modal when id changes (equivalent to componentDidUpdate)
  useEffect(() => {
    setIsModalOpen(false);
    setModal(null);
    setSnapshot(null);
  }, [id]);

  // --- Helpers ---
  const getEditing = useCallback((): EditingEntry | null => {
    if (!modal || modal.editingIndex === null) return null;
    return modal.editingIndex === -1 ? modal.baseDefault : modal.bindings[modal.editingIndex];
  }, [modal]);

  const hasChanges = useCallback((): boolean => {
    if (!modal || !snapshot) return false;
    const orig = JSON.parse(snapshot) as ModalState;
    const b = modal.baseDefault;
    const ob = orig.baseDefault;
    if (b.minValue !== ob.minValue || b.maxValue !== ob.maxValue ||
        b.itemCount !== ob.itemCount || b.swDefaultValue !== ob.swDefaultValue) return true;
    if (modal.bindings.length !== orig.bindings.length) return true;
    return modal.bindings.some((binding, i) => {
      const origBinding = orig.bindings[i];
      return binding.kceVersion.id !== origBinding.kceVersion.id ||
             binding.kceProduct.id !== origBinding.kceProduct.id ||
             binding.minValue !== origBinding.minValue ||
             binding.maxValue !== origBinding.maxValue ||
             binding.itemCount !== origBinding.itemCount ||
             binding.swDefaultValue !== origBinding.swDefaultValue;
    });
  }, [modal, snapshot]);

  const hasErrors = useCallback((): boolean => {
    if (!modal) return false;
    const ascii = isAscii(editorDefines);
    const bErr = validate(modal.baseDefault.minValue, modal.baseDefault.maxValue, modal.baseDefault.itemCount, modal.baseDefault.swDefaultValue, ascii);
    if (bErr.minMaxErr || bErr.itmErr || bErr.rangeErr) return true;
    if (getDuplicateLiftParameterBindings(modal.bindings).length > 0) return true;
    return modal.bindings.some(b => {
      const e = validate(b.minValue, b.maxValue, b.itemCount, b.swDefaultValue, ascii);
      return !!(e.minMaxErr || e.itmErr || e.rangeErr);
    });
  }, [modal, editorDefines]);

  // --- Modal lifecycle ---
  const openModal = useCallback((): void => {
    const base: EditingEntry = { minValue, maxValue, itemCount, swDefaultValue: swDefaultValues };
    const bindings = liftParameterValues.map(lv => {
      const resolved = resolveEntry(lv, minValue, maxValue, itemCount);
      return { ...lv, ...resolved };
    });
    const newModal: ModalState = { baseDefault: base, bindings, editingIndex: -1, pendingItemCount: itemCount };
    setIsModalOpen(true);
    setModal(newModal);
    setSnapshot(JSON.stringify(newModal));
  }, [swDefaultValues, minValue, maxValue, itemCount, liftParameterValues]);

  const closeModal = useCallback((): void => {
    setIsModalOpen(false);
    setModal(null);
    setSnapshot(null);
    setConfirmClose(false);
  }, []);

  const requestClose = useCallback((): void => {
    if (hasChanges()) {
      setConfirmClose(true);
    } else {
      closeModal();
    }
  }, [hasChanges, closeModal]);

  const commitChanges = useCallback((): void => {
    if (!modal) return;
    const ascii = isAscii(editorDefines);
    const bErr = validate(modal.baseDefault.minValue, modal.baseDefault.maxValue, modal.baseDefault.itemCount, modal.baseDefault.swDefaultValue, ascii);
    if (bErr.minMaxErr || bErr.itmErr || bErr.rangeErr) return;
    for (const b of modal.bindings) {
      const e = validate(b.minValue, b.maxValue, b.itemCount, b.swDefaultValue, ascii);
      if (e.minMaxErr || e.itmErr || e.rangeErr) return;
    }
    if (getDuplicateLiftParameterBindings(modal.bindings).length > 0) return;
    const out: Api.LiftParameterValue[] = modal.bindings.map(b => ({
      liftParameterCode: b.liftParameterCode, kceProduct: b.kceProduct, kceVersion: b.kceVersion,
      swDefaultValue: b.swDefaultValue, minValue: b.minValue, maxValue: b.maxValue, itemCount: b.itemCount
    }));
    handleDefaultValuesChanged(modal.baseDefault.swDefaultValue, out, modal.baseDefault.minValue, modal.baseDefault.maxValue, modal.baseDefault.itemCount);
    closeModal();
  }, [modal, editorDefines, handleDefaultValuesChanged, closeModal]);

  // --- State updaters ---
  const updateEditing = useCallback((u: Partial<EditingEntry>): void => {
    setModal(p => {
      if (!p || p.editingIndex === null) return p;
      const m = { ...p };
      if (m.editingIndex === -1) m.baseDefault = { ...m.baseDefault, ...u };
      else m.bindings = m.bindings.map((b, i) => i === m.editingIndex ? { ...b, ...u } : b);
      return m;
    });
  }, []);

  const selectEditing = useCallback((idx: number): void => {
    setModal(p => {
      if (!p) return p;
      const sel = idx === -1 ? p.baseDefault : p.bindings[idx];
      return { ...p, editingIndex: idx, pendingItemCount: sel?.itemCount ?? 1 };
    });
  }, []);

  const applyItemCount = useCallback((): void => {
    if (!modal || modal.pendingItemCount < 1) return;
    const sel = getEditing();
    if (!sel) return;
    updateEditing({ itemCount: modal.pendingItemCount, swDefaultValue: resize(sel.swDefaultValue, modal.pendingItemCount, sel.minValue) });
  }, [modal, getEditing, updateEditing]);

  const handleSwChanged = useCallback((idx: number, val: number): void => {
    setModal(p => {
      if (!p || p.editingIndex === null) return p;
      const sel = p.editingIndex === -1 ? p.baseDefault : p.bindings[p.editingIndex];
      if (!sel) return p;
      const d = createSwDefaultArray(sel.swDefaultValue);
      if (idx === 0) d.forEach((_, i) => { d[i] = val.toString(); });
      d[idx] = val.toString();
      const updated = { swDefaultValue: d.join(";") };
      const m = { ...p };
      if (m.editingIndex === -1) m.baseDefault = { ...m.baseDefault, ...updated };
      else m.bindings = m.bindings.map((b, i) => i === m.editingIndex ? { ...b, ...updated } : b);
      return m;
    });
  }, []);

  const handleStringChanged = useCallback((val: string): void => {
    // Truncate input to MAX_ITEM_COUNT characters — do not accept more
    const truncated = val.slice(0, MAX_ITEM_COUNT);
    if (truncated.length === 0) {
      updateEditing({ swDefaultValue: "0", itemCount: 1, minValue: 0, maxValue: 0 });
      setModal(p => p ? { ...p, pendingItemCount: 1 } : p);
    } else {
      const len = Math.max(50, truncated.length);
      const sel = getEditing();
      // Pad remaining slots with 0 so swDefaultValue length always matches itemCount
      const paddedSw = resize(asciiToSw(truncated), len, 0);
      const updates: Partial<EditingEntry> = { swDefaultValue: paddedSw, itemCount: len };
      if (!sel || sel.maxValue === 0) updates.maxValue = 4294967295;
      updateEditing(updates);
      setModal(p => p ? { ...p, pendingItemCount: len } : p);
    }
  }, [updateEditing, getEditing]);

  const addBinding = useCallback((): void => {
    setModal(p => {
      if (!p) return p;
      const m = { ...p };
      const ascii = isAscii(editorDefines);
      m.bindings = [{
        liftParameterCode: code,
        kceProduct: { id: -1, url: "" }, kceVersion: { id: -1, url: "" },
        swDefaultValue: m.baseDefault.swDefaultValue,
        minValue: m.baseDefault.minValue,
        maxValue: ascii ? 4294967295 : m.baseDefault.maxValue,
        itemCount: m.baseDefault.itemCount
      }, ...m.bindings];
      m.editingIndex = 0;
      m.pendingItemCount = m.baseDefault.itemCount;
      return m;
    });
  }, [code, editorDefines]);

  const removeBinding = useCallback((idx: number): void => {
    setModal(p => {
      if (!p) return p;
      const m = { ...p };
      m.bindings = m.bindings.filter((_, i) => i !== idx);
      if (m.editingIndex === idx) { m.editingIndex = -1; m.pendingItemCount = m.baseDefault.itemCount; }
      else if (m.editingIndex !== null && m.editingIndex > idx) { m.editingIndex--; }
      return m;
    });
  }, []);

  const updateBindingField = useCallback((idx: number, u: Partial<Api.LiftParameterValue>): void => {
    setModal(p => {
      if (!p) return p;
      const m = { ...p };
      m.bindings = m.bindings.map((b, i) => i === idx ? { ...b, ...u } : b);
      const sel = m.bindings[idx];
      m.editingIndex = idx;
      m.pendingItemCount = sel?.itemCount ?? m.pendingItemCount;
      return m;
    });
  }, []);

  const toggleAccordion = useCallback((): void => {
    setIsAccordionOpen(p => !p);
  }, []);

  // --- Render helpers ---
  const ascii = isAscii(editorDefines);
  const baseErr = validate(minValue, maxValue, itemCount, swDefaultValues, ascii);
  const dups = getDuplicateLiftParameterBindings(liftParameterValues);
  const anyBErr = liftParameterValues.some(lv => {
    const r = resolveEntry(lv, minValue, maxValue, itemCount);
    const e = validate(r.minValue, r.maxValue, r.itemCount, r.swDefaultValue, ascii);
    return !!(e.minMaxErr || e.itmErr || e.rangeErr);
  });
  const summary = baseErr.minMaxErr || baseErr.itmErr || baseErr.rangeErr
    || (dups.length > 0 ? "Bindings should not be empty or duplicated. Please select unique Version and Product for each binding." : "")
    || (anyBErr ? "One or more bindings have errors. Please open Edit Sw Default Value to review and fix." : "");

  return (
    <Form.Group id="liftDefaultValues" grouped={true}>
      <Form.Field>
        <Accordion fluid={true} styled={true}>
          <Accordion.Title active={isAccordionOpen} onClick={toggleAccordion} aria-expanded={isAccordionOpen}>
            <Icon name="dropdown" />
            <span><b>Default values</b></span>
            {!isAccordionOpen && summary && (
                <p aria-live="polite" aria-atomic="true" className="errorMessagePadding">{summary}</p>
              )}
          </Accordion.Title>
          <Accordion.Content active={isAccordionOpen}>
            {summary && (
                <p aria-live="polite" aria-atomic="true" className="errorMessagePadding">{summary}</p>
              )}
            <div className={styles.summaryRow}>
              <Button id="editSwDefaults" size="small" color="blue" onClick={openModal}>
                Edit Sw Default Value
              </Button>
            </div>
          </Accordion.Content>
        </Accordion>
      </Form.Field>
      {isModalOpen && modal && renderModal()}
    </Form.Group>
  );

  function renderModal(): JSX.Element {
    if (!modal) return <></>;
    const ascii = isAscii(editorDefines);
    const modalDups = getDuplicateLiftParameterBindings(modal.bindings);
    const vOpts = values(kceVersions).map(v => ({ key: v.id, text: v.name, value: v.id }));
    const pOpts = values(kceProducts).map(p => ({ key: p.id, text: p.productName, value: p.id }));
    const sel = getEditing();
    const selErrors = sel ? validate(sel.minValue, sel.maxValue, sel.itemCount, sel.swDefaultValue, ascii) : null;
    const pendingDiffers = sel ? modal.pendingItemCount !== sel.itemCount : false;
    const origBindings = snapshot ? (JSON.parse(snapshot) as ModalState).bindings : [];

    return (
      <>
        <Modal size="large" open={true} role="dialog" aria-modal="true" aria-labelledby="edit-sw-modal-title" className={`data-sections-modal ${styles.splitModal}`}>
          <div className="modal-header">
            <p id="edit-sw-modal-title" className="title">Edit Sw Default Value</p>
            <div className="edit-header-actions">
              {hasChanges() && !pendingDiffers && <MenuButton type="Save" handleOnClick={commitChanges} disabled={hasErrors()} />}
              <MenuButton type="Close" handleOnClick={requestClose} />
            </div>
          </div>
          <Modal.Content scrolling={false}>
            <div className={styles.splitLayout}>
              {/* LEFT: bindings list */}
              <div className={styles.leftPanel}>
                <div className={styles.leftFixed}>
                  <Table compact="very" size="small" className={styles.headerTable}>
                    <Table.Header>
                      <Table.Row>
                        <Table.HeaderCell>Selection</Table.HeaderCell>
                        <Table.HeaderCell>Version</Table.HeaderCell>
                        <Table.HeaderCell>Product</Table.HeaderCell>
                        <Table.HeaderCell textAlign="right">
                          {!permissions && (
                            <Popup trigger={<Icon id="addBinding" aria-label="Add binding" className="color-green" link={true} name="add" onClick={addBinding} />} content="Add binding" />
                          )}
                        </Table.HeaderCell>
                      </Table.Row>
                    </Table.Header>
                  </Table>
                </div>
                <div className={styles.leftScroll}>
                  <Table compact="very" size="small">
                    <Table.Body>
                      <Table.Row active={modal.editingIndex === -1} className={styles.defaultRow}>
                        <Table.Cell>
                          <Popup
                            trigger={
                              <div className={styles.radioTrigger}>
                                <Radio checked={modal.editingIndex === -1} onChange={() => selectEditing(-1)} />
                                <span>Default</span>
                              </div>
                            }
                            content="Applied to all bindings when no binding-specific SW values are defined."
                          />
                        </Table.Cell>
                        <Table.Cell />
                        <Table.Cell />
                        <Table.Cell />
                      </Table.Row>
                      {modalDups.length > 0 && (
                        <Table.Row>
                          <Table.Cell colSpan={4} className={styles.dupErrorCell}>
                            <p aria-live="polite" aria-atomic="true" className={styles.errorText}>Bindings should not be empty or duplicated.</p>
                          </Table.Cell>
                        </Table.Row>
                      )}
                      {modal.bindings.map((b, i) => {
                        const isDup = modalDups.includes(i);
                        const bErr = validate(b.minValue, b.maxValue, b.itemCount, b.swDefaultValue, ascii);
                        const hasErr = isDup || !!(bErr.minMaxErr || bErr.itmErr || bErr.rangeErr);
                        const origBinding = origBindings.find(
                          o => o.kceVersion.id === b.kceVersion.id && o.kceProduct.id === b.kceProduct.id
                        );
                        const isNewOrEdited = !origBinding ||
                          b.swDefaultValue !== origBinding.swDefaultValue ||
                          b.minValue !== origBinding.minValue ||
                          b.maxValue !== origBinding.maxValue ||
                          b.itemCount !== origBinding.itemCount;
                        return (
                          <Table.Row key={i} active={modal.editingIndex === i}>
                            <Table.Cell>
                              <Radio checked={modal.editingIndex === i} onChange={() => selectEditing(i)} />
                              {isNewOrEdited && <span className={styles.newStar}> *</span>}
                            </Table.Cell>
                            <Table.Cell>
                              <Dropdown id={`ver-${i}`} error={hasErr} compact={true} fluid={true} selection={true}
                                value={b.kceVersion.id} options={vOpts}
                                onChange={(_e, data) => {
                                  if (typeof data.value !== "number") return;
                                  const ver = kceVersions[data.value];
                                  if (ver) updateBindingField(i, { kceVersion: { id: ver.id, url: ver.url } });
                                }} disabled={permissions} />
                            </Table.Cell>
                            <Table.Cell>
                              <Dropdown id={`prod-${i}`} error={hasErr} compact={true} fluid={true} selection={true}
                                value={b.kceProduct.id} options={pOpts}
                                onChange={(_e, data) => {
                                  if (typeof data.value !== "number") return;
                                  const prod = kceProducts[data.value];
                                  if (prod) updateBindingField(i, { kceProduct: { id: prod.id, url: prod.url } });
                                }} disabled={permissions} />
                            </Table.Cell>
                            <Table.Cell textAlign="center">
                              {!permissions && (
                                <Popup
                                  trigger={<Icon id={`rm-${i}`} aria-label="Remove binding" className="color-red" link={true} name="remove" onClick={() => removeBinding(i)} />}
                                  content="Delete binding"
                                />
                              )}
                            </Table.Cell>
                          </Table.Row>
                        );
                      })}
                      {modal.bindings.length === 0 && (
                        <Table.Row>
                          <Table.Cell colSpan={4} textAlign="center" className={styles.noBindingsCell}>
                            Only default Sw values found.
                            <br />
                            Click + to add version/product specific Sw values.
                          </Table.Cell>
                        </Table.Row>
                      )}
                    </Table.Body>
                  </Table>
                </div>
              </div>

              {/* RIGHT: edit panel */}
              <div className={styles.rightPanel}>
                {sel !== null && selErrors !== null && (
                  <>
                    <div className={styles.rightFixed}>
                      {modal.editingIndex !== null && modal.editingIndex >= 0 && (() => {
                        const b = modal.bindings[modal.editingIndex];
                        const vName = kceVersions[b.kceVersion.id]?.name ?? "—";
                        const pName = kceProducts[b.kceProduct.id]?.productName ?? "—";
                        return (
                          <div className={styles.bindingInfoBar}>
                            <span><span className={styles.infoLabel}>Version</span> {vName}</span>
                            <span><span className={styles.infoLabel}>Product</span> {pName}</span>
                          </div>
                        );
                      })()}
                      {modal.editingIndex === -1 && <div className={styles.bindingInfoBar}><span><b>Default</b></span></div>}
                      <div className={styles.rangeRow}>
                        {/* Min value: lower bound for all Sw default entries. For ASCII parameters, must be 0 to allow padding zeros to pass validation. */}
                        <NumberField id="mMin" label="Min value" value={sel.minValue} isError={sel.minValue > sel.maxValue}
                          onChange={v => updateEditing({ minValue: v })} disabled={permissions} />
                        {/* Max value: upper bound for all Sw default entries. For ASCII parameters, set to 4294967295 if the range is unknown. */}
                        <NumberField id="mMax" label="Max value" value={sel.maxValue} isError={sel.minValue > sel.maxValue}
                          onChange={v => updateEditing({ maxValue: v })} disabled={permissions} />
                        {ascii ? (
                          // Item count is auto-managed for ASCII — driven by the length of the entered string (padded to min 50 slots).
                          <Popup
                            trigger={
                              <span>
                                <NumberField id="mItm" label="Item count" value={modal.pendingItemCount} isError={modal.pendingItemCount < 1 || modal.pendingItemCount > MAX_ITEM_COUNT}
                                  onChange={v => setModal(p => p ? { ...p, pendingItemCount: v } : p)}
                                  disabled={true} />
                              </span>
                            }
                            content={`Item Count cannot exceed ${MAX_ITEM_COUNT}.`}
                          />
                        ) : (
                          <NumberField id="mItm" label="Item count" value={modal.pendingItemCount} isError={modal.pendingItemCount < 1 || modal.pendingItemCount > MAX_ITEM_COUNT}
                            onChange={v => setModal(p => p ? { ...p, pendingItemCount: v } : p)}
                            disabled={permissions} />
                        )}
                        {!ascii && !permissions && (
                          // Apply Count: resizes the swDefaultValue array to match the new item count, filling new slots with minValue.
                          <Form.Field className={styles.applyField}>
                            <span aria-hidden="true">&nbsp;</span>
                            <Popup
                              trigger={
                                <span>
                                  <Button color="blue"
                                    disabled={!pendingDiffers || modal.pendingItemCount < 1 || modal.pendingItemCount > MAX_ITEM_COUNT}
                                    className={`${styles.applyBtn} ${(!pendingDiffers || modal.pendingItemCount < 1 || modal.pendingItemCount > MAX_ITEM_COUNT) ? styles.editBtnDisabled : ""}`}
                                    onClick={applyItemCount}>Apply Count</Button>
                                </span>
                              }
                              content="Click to confirm the Item Count"
                            />
                          </Form.Field>
                        )}
                      </div>
                      {/* Show hint below the row for ASCII when user has changed max away from the default 4294967295 */}
                      {ascii && sel.maxValue !== 4294967295 && (
                        <small aria-live="polite" style={{ color: "#888" }}>Max value default: 4294967295</small>
                      )}
                      <div aria-live="polite" aria-atomic="true">
                        {selErrors.minMaxErr && <p className={styles.errorText}>{selErrors.minMaxErr}</p>}
                        {selErrors.itmErr && <p className={styles.errorText}>{selErrors.itmErr}</p>}
                        {modal.pendingItemCount > MAX_ITEM_COUNT && <p className={styles.errorText}>{`Item Count cannot exceed ${MAX_ITEM_COUNT}.`}</p>}
                        {selErrors.rangeErr && <p className={styles.errorText}>{selErrors.rangeErr}</p>}
                      </div>
                    </div>
                    <div className={styles.rightScroll}>
                      {!ascii && (
                        // Sw default value table: each row corresponds to one index in the semicolon-separated swDefaultValue string.
                        // All values (including any zeros) are validated against Min value and Max value.
                        <div className={styles.swTableWrap}>
                          <Table compact="very" size="small">
                            <Table.Header>
                              <Table.Row>
                                <Table.HeaderCell width={3}>Index</Table.HeaderCell>
                                <Table.HeaderCell>
                                  Sw default value
                                  {pendingDiffers && modal.pendingItemCount >= 1 && modal.pendingItemCount <= MAX_ITEM_COUNT && (
                                    <span className={styles.pendingHint}>
                                      Click "Apply Count" to update the item count.
                                    </span>
                                  )}
                                </Table.HeaderCell>
                              </Table.Row>
                            </Table.Header>
                            <Table.Body>
                              {sel.swDefaultValue.split(";").map((v, idx) => (
                                <Table.Row key={idx}>
                                  <Table.Cell>{idx + 1}</Table.Cell>
                                  <Table.Cell>
                                    <NumberField id={`sw-${idx}`} value={Number(v)}
                                      isError={Number(v) < sel.minValue || Number(v) > sel.maxValue}
                                      onChange={val => handleSwChanged(idx, val)} disabled={permissions} />
                                  </Table.Cell>
                                </Table.Row>
                              ))}
                            </Table.Body>
                          </Table>
                        </div>
                      )}
                      {ascii && (
                        // ASCII mode: user enters a plain string. It is converted to char codes (e.g. "Hi" → "72;105").
                        // Remaining slots up to itemCount are padded with 0. All values including padding zeros
                        // are validated against Min value and Max value — set Min to 0 to allow padding zeros to pass.
                        <div className={styles.asciiWrap}>
                          <FormTextArea id="asciiSw" label="Sw Default Value" value={swToAscii(sel.swDefaultValue)}
                            maxLength={MAX_ITEM_COUNT}
                            onChange={(_e, { value }: TextAreaProps) => handleStringChanged(typeof value === "string" ? value : "")} disabled={permissions} />
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </Modal.Content>
        </Modal>
        {confirmClose && (
          <Modal size="mini" open={true} role="dialog" aria-modal="true" aria-labelledby="confirm-close-title" style={{ marginTop: "20vh" }}>
            <Modal.Header id="confirm-close-title">Discard changes?</Modal.Header>
            <Modal.Content>
              <p>You have unsaved changes. Are you sure you want to close without saving?</p>
            </Modal.Content>
            <Modal.Actions>
              <Button negative onClick={closeModal}>Discard</Button>
              <Button onClick={() => setConfirmClose(false)}>Cancel</Button>
            </Modal.Actions>
          </Modal>
        )}
      </>
    );
  }
};

export default LiftParameterValues;
